import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { parseAsInteger, useQueryStates } from 'nuqs'
import { toast } from 'sonner'
import AllRoles from '../../components/AllRoles/AllRoles'
import Pagination from '@/shared/components/Pagination/Pagination'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog/ConfirmDialog'
import { Loader } from '@/shared/components/Loader/Loader'
import { Separator } from '@/ui/separator'
import { useDebounce } from '@/shared/hooks'
import { useRolesQuery } from '../../queries/useRoleQueries'
import {
  useDeleteRoleMutation,
  useDuplicateRoleMutation,
} from '../../mutations/useRoleMutations'
import type { RoleListItem } from '../../services/roles/roles.types'
import styles from '../RolePage/Rolepage.module.scss'

export default function RoleActivePage() {
  const navigate = useNavigate()

  const [{ page, pageSize }, setPagination] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
  })

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const [selectedRole, setSelectedRole] = useState<RoleListItem | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data, isLoading, isError, isFetching, refetch } = useRolesQuery({
    isActive: '1',
    pageNumber: page,
    limit: pageSize,
    searchName: debouncedSearch || undefined,
    sortBy: 'created_date',
    sortOrder: 'desc',
  })

  const roles = data?.roles ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const deleteRoleMutation = useDeleteRoleMutation({
    onSuccess: () => toast.success('Role deleted successfully'),
    onError: (error) => toast.error(error.message),
  })

  const duplicateRoleMutation = useDuplicateRoleMutation({
    onSuccess: () => toast.success('Role duplicated successfully'),
    onError: (error) => toast.error(error.message),
  })

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (page !== 1) setPagination({ page: 1 })
  }

  const handleEdit = (role: RoleListItem) => {
    navigate(`/roles/${role.id}/edit`)
  }

  const handleDuplicate = (role: RoleListItem) => {
    duplicateRoleMutation.mutate(role.id)
  }

  const handleDelete = (role: RoleListItem) => {
    setSelectedRole(role)
    setConfirmOpen(true)
  }

  const isSelectedDefault = selectedRole?.role_type === 'default'

  const closeConfirm = () => {
    setConfirmOpen(false)
    setSelectedRole(null)
  }

  const handleConfirmDelete = () => {
    if (selectedRole && !isSelectedDefault) {
      deleteRoleMutation.mutate(selectedRole.id)
    }
    closeConfirm()
  }

  return (
    <div>
      {isLoading ? (
        <Loader text="Loading active roles..." />
      ) : isError ? (
        <p className={styles.error}>Error loading active roles</p>
      ) : (
        <>
          <div className={styles.actionHeaderWrapper}>
            <ActionHeader
              title="Active Roles"
              search={search}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search Roles"
              actions={[
                {
                  icon: 'refresh',
                  onClick: () => refetch(),
                  variant: 'icon',
                  ariaLabel: 'Refresh',
                  key: 'refresh-roles',
                },
                {
                  icon: 'add',
                  label: 'Create Role',
                  onClick: () => navigate('/roles/create'),
                  variant: 'primary',
                  key: 'create-role',
                },
              ]}
            />
          </div>
          <Separator />
          <div className={styles.tableWrapper}>
            {roles.length === 0 ? (
              <p className={styles.error}>
                {isFetching ? 'Loading roles...' : 'No roles found'}
              </p>
            ) : (
              <>
                <AllRoles
                  mode="active"
                  roles={roles}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalRecords={total}
                  rowsPerPage={pageSize}
                  onPageChange={(next) => setPagination({ page: next })}
                  onRowsPerPageChange={(size) =>
                    setPagination({ pageSize: size, page: 1 })
                  }
                />
              </>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={isSelectedDefault ? 'Cannot delete default role' : 'Delete role'}
        message={
          isSelectedDefault
            ? 'Default roles cannot be deleted. Please choose a custom role to remove.'
            : `Delete role "${selectedRole?.title}"? It will be moved to the Deleted tab.`
        }
        confirmText={isSelectedDefault ? 'Okay' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={closeConfirm}
      />
    </div>
  )
}
